import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { TelecallerService } from '../../services/telecallerService';
import { Phone, History, CheckCircle, BookOpen, MoreHorizontal, Search, Filter, PhoneCall, Clock, User, MapPin, BarChart3, Settings, HelpCircle } from 'lucide-react';
import { maskPhoneLast4 } from '../../utils/phoneMask';

// Import new screens
import TelecallerProfile from './Profile';
import TelecallerSettings from './Settings';
import TelecallerPerformance from './Performance';
import CallRecordings from './CallRecordings';
import TelecallerHelpSupport from './HelpSupport';

const TelecallerDialer = () => {
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [callNotes, setCallNotes] = useState('');
  const [callStatus, setCallStatus] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [isLogging, setIsLogging] = useState(false);
  
  const { data: leadsData, refetch } = useQuery({
    queryKey: ['telecallerLeads', user?.id],
    queryFn: () => TelecallerService.getAssignedLeads(user?.id),
    enabled: !!user?.id,
  });
  const leads = leadsData?.data || [];

  const handleCall = (lead) => {
    setSelectedLead(lead);
    setPhoneNumber(lead.contact_number || '');
  };

  const handleEndCall = async () => {
    if (!selectedLead || !callStatus) {
      alert('Please select call status');
      return;
    }

    setIsLogging(true);
    try {
      const result = await TelecallerService.logCall(
        selectedLead.id,
        user?.id,
        callStatus,
        callNotes
      );

      if (result.success) {
        alert('Call logged successfully!');
        setPhoneNumber('');
        setCallNotes('');
        setCallStatus('');
        setSelectedLead(null);
        refetch();
      } else {
        alert('Failed to log call: ' + result.error);
      }
    } catch (error) {
      alert('Error logging call: ' + error.message);
    } finally {
      setIsLogging(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dialer</h1>
        <p className="text-gray-600 mt-1">Make calls and log activities</p>
      </div>

      {/* Dialer Interface */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Assigned Leads</h2>
          <div className="space-y-3">
            {leads.slice(0, 5).map((lead) => (
              <div key={lead.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{lead.name || 'Unknown'}</h3>
                  <p className="text-sm text-gray-600">{maskPhoneLast4(lead.contact_number) || 'N/A'}</p>
                  {lead.address && <p className="text-xs text-gray-500 mt-1">{lead.address}</p>}
                </div>
                <button
                  onClick={() => handleCall(lead)}
                  className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  <Phone size={20} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Call Log</h2>
          {phoneNumber ? (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Calling</p>
                <p className="text-lg font-semibold text-gray-900">{selectedLead?.name || 'Unknown'}</p>
                <p className="text-sm text-gray-600">{maskPhoneLast4(phoneNumber) || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Call Status</label>
                <select
                  value={callStatus}
                  onChange={(e) => setCallStatus(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Select status</option>
                  <option value="answered">Answered</option>
                  <option value="no_answer">No Answer</option>
                  <option value="not_interested">Not Interested</option>
                  <option value="callback">Callback</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  value={callNotes}
                  onChange={(e) => setCallNotes(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Add call notes..."
                />
              </div>
              <button
                onClick={handleEndCall}
                disabled={isLogging || !callStatus}
                className="w-full px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
              >
                {isLogging ? 'Logging...' : 'End Call & Log'}
              </button>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <PhoneCall size={48} className="mx-auto mb-4 text-gray-400" />
              <p>Select a lead to start calling</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TelecallerLeadsHistory = () => {
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const [searchQuery, setSearchQuery] = useState('');
  
  const { data: leadsHistoryData, isLoading } = useQuery({
    queryKey: ['telecallerLeadsHistory', user?.id],
    queryFn: () => TelecallerService.getLeadsHistory(user?.id),
    enabled: !!user?.id,
  });
  const leadsHistory = leadsHistoryData?.data || [];

  const filteredLeads = leadsHistory.filter(lead =>
    lead.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lead.contact_number?.includes(searchQuery)
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Leads History</h1>
          <p className="text-gray-600 mt-1">View all your lead interactions</p>
        </div>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search leads..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
            <p className="text-gray-600">Loading history...</p>
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="p-12 text-center">
            <History size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">No leads found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredLeads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          <User size={20} className="text-blue-600" />
                        </div>
                        <span className="font-medium text-gray-900">{lead.name || 'Unknown'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">{maskPhoneLast4(lead.contact_number) || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-700">
                        {lead.status || 'Pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                      {lead.updated_at ? new Date(lead.updated_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button className="text-blue-600 hover:text-blue-800">
                        <Phone size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const TelecallerConfirmation = () => {
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [isConfirming, setIsConfirming] = useState(false);
  
  const { data: leadsData, refetch } = useQuery({
    queryKey: ['telecallerConfirmationLeads', user?.id],
    queryFn: () => TelecallerService.getConfirmationLeads(user?.id),
    enabled: !!user?.id,
  });
  const leads = leadsData?.data || [];

  const confirmLeads = async () => {
    if (selectedLeads.length === 0) {
      alert('Please select at least one lead');
      return;
    }

    setIsConfirming(true);
    try {
      const result = await TelecallerService.confirmLeads(selectedLeads, user?.id);

      if (result.success) {
        alert('Leads confirmed successfully!');
        setSelectedLeads([]);
        refetch();
      } else {
        alert('Failed to confirm leads: ' + result.error);
      }
    } catch (error) {
      alert('Error confirming leads: ' + error.message);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Lead Confirmation</h1>
          <p className="text-gray-600 mt-1">Confirm leads for order processing</p>
        </div>
        <button
          onClick={confirmLeads}
          disabled={selectedLeads.length === 0 || isConfirming}
          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
        >
          {isConfirming ? 'Confirming...' : `Confirm Selected (${selectedLeads.length})`}
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6">
          <div className="space-y-3">
            {leads.slice(0, 10).map((lead) => (
              <div key={lead.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={selectedLeads.includes(lead.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedLeads([...selectedLeads, lead.id]);
                      } else {
                        setSelectedLeads(selectedLeads.filter(id => id !== lead.id));
                      }
                    }}
                    className="w-5 h-5"
                  />
                  <div>
                    <h3 className="font-semibold text-gray-900">{lead.name || 'Unknown'}</h3>
                    <p className="text-sm text-gray-600">{maskPhoneLast4(lead.contact_number) || 'N/A'}</p>
                  </div>
                </div>
                <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700">
                  {lead.status || 'Pending'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const TelecallerBooked = () => {
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const navigate = useNavigate();
  
  const { data: bookedOrdersData, isLoading } = useQuery({
    queryKey: ['telecallerBookedOrders', user?.id],
    queryFn: () => TelecallerService.getBookedOrders(user?.id),
    enabled: !!user?.id,
  });
  const bookedOrders = bookedOrdersData?.data || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Booked Orders</h1>
        <p className="text-gray-600 mt-1">View all your booked orders</p>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading orders...</p>
        </div>
      ) : bookedOrders.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <BookOpen size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No booked orders yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {bookedOrders.map((order) => (
                <div key={order.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 cursor-pointer hover:shadow-md transition"
                  onClick={() => navigate(`/order/${order.order_id || order.id}`)}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900">Order #{order.id?.slice(0, 8)}</h3>
                    <CheckCircle size={20} className="text-green-600" />
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Customer:</span> {order.customer_name || 'N/A'}</p>
                    <p><span className="font-medium">Contact:</span> {maskPhoneLast4(order.contact_number) || 'N/A'}</p>
                    <p><span className="font-medium">Amount:</span> ₹{Number(order.amount || 0).toLocaleString('en-IN')}</p>
                    <p><span className="font-medium">Date:</span> {order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <button className="mt-4 w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm">
                    View Details
                  </button>
                </div>
              ))}
        </div>
      )}
    </div>
  );
};

const TelecallerMore = () => {
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const navigate = useNavigate();

  const menuItems = [
    {
      title: 'Profile',
      subtitle: 'View and edit your profile',
      icon: User,
      color: 'bg-blue-100 text-blue-700',
      onClick: () => navigate('/telecaller/profile'),
    },
    {
      title: 'Performance',
      subtitle: 'View your performance metrics',
      icon: BarChart3,
      color: 'bg-green-100 text-green-700',
      onClick: () => navigate('/telecaller/performance'),
    },
    {
      title: 'Settings',
      subtitle: 'App preferences and configuration',
      icon: Settings,
      color: 'bg-gray-100 text-gray-700',
      onClick: () => navigate('/telecaller/settings'),
    },
    {
      title: 'Call Recordings',
      subtitle: 'Access your call recordings',
      icon: Phone,
      color: 'bg-purple-100 text-purple-700',
      onClick: () => navigate('/telecaller/call-recordings'),
    },
    {
      title: 'Help & Support',
      subtitle: 'Get help and contact support',
      icon: HelpCircle,
      color: 'bg-yellow-100 text-yellow-700',
      onClick: () => navigate('/telecaller/help'),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">More Options</h1>
        <p className="text-gray-600 mt-1">Additional features and settings</p>
      </div>

      {/* Profile Section */}
      <div className="bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl p-6 text-white">
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
            <span className="text-white font-bold text-2xl">
              {user?.name?.charAt(0).toUpperCase() || 'T'}
            </span>
          </div>
          <div>
            <h2 className="text-2xl font-bold">{user?.name || 'Telecaller'}</h2>
            <p className="text-white/80">{user?.email || 'No email'}</p>
            <p className="text-sm text-white/70">Telecaller</p>
          </div>
        </div>
      </div>

      {/* Menu Items */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="space-y-2">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={index}
                onClick={item.onClick}
                className="w-full flex items-center justify-between p-4 rounded-lg hover:bg-gray-50 transition"
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${item.color}`}>
                    <Icon size={20} />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-900">{item.title}</h3>
                    <p className="text-sm text-gray-500">{item.subtitle}</p>
                  </div>
                </div>
                <MoreHorizontal size={20} className="text-gray-400" />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export { TelecallerDialer, TelecallerLeadsHistory, TelecallerConfirmation, TelecallerBooked, TelecallerMore };

